import { Card } from "@amzn/stencil-react-components/card";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { InputFooter, InputHeader } from "@amzn/stencil-react-components/input";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { MessageBanner } from "@amzn/stencil-react-components/message-banner";
import { Select } from "@amzn/stencil-react-components/select";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { useController, useFormContext } from "react-hook-form";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { FormField } from "@/common/components/FormField/form-field";
import { ComboBoxInput } from "@/common/components/ComboBoxInput/combo-box-input";
import type { CheckoutFormState } from "@/pages/Checkout/checkout-form.types";
import {
  REQUIRED_FIELD_ERROR,
  validateAddressLine3,
  validateRequiredString,
} from "@/pages/Checkout/checkout-form.util";
import { useChangeShippingDetailsCard } from "@/pages/Checkout/components/ChangeShippingDetailsCard/hooks/use-change-shipping-details-card.hook";
import React from "react";

const STRING_PATH = "checkout.formPanel.shippingDetailsCard";
const PHONE_NUMBER_REGEX = /^[0-9\s\-().]+$/;

export const ChangeShippingDetailsCard: React.FC = () => {
  const { control } = useFormContext<CheckoutFormState>();
  const { field: fullNameField, fieldState: fullNameState } = useController<
    CheckoutFormState,
    "fullName"
  >({
    control,
    name: "fullName",
    rules: { validate: validateRequiredString },
  });
  const { field: addressLine1Field, fieldState: addressLine1State } = useController<
    CheckoutFormState,
    "addressLine1"
  >({
    control,
    name: "addressLine1",
    rules: { validate: validateRequiredString },
  });
  const { field: addressLine2Field, fieldState: addressLine2State } = useController<
    CheckoutFormState,
    "addressLine2"
  >({
    control,
    name: "addressLine2",
    rules: { deps: ["addressLine3"] },
  });
  const { field: addressLine3Field, fieldState: addressLine3State } = useController<
    CheckoutFormState,
    "addressLine3"
  >({
    control,
    name: "addressLine3",
    rules: { validate: validateAddressLine3 },
  });
  const { field: postalCodeField, fieldState: postalCodeState } = useController<
    CheckoutFormState,
    "postalCode"
  >({
    control,
    name: "postalCode",
    rules: { validate: validateRequiredString },
  });
  const { field: phoneNumberField, fieldState: phoneNumberState } = useController<
    CheckoutFormState,
    "phoneNumber"
  >({
    control,
    name: "phoneNumber",
    rules: {
      validate: {
        required: validateRequiredString,
        pattern: (value) =>
          PHONE_NUMBER_REGEX.test(value) || getString(`${STRING_PATH}.invalidPhoneNumber`),
      },
    },
  });
  const { field: deliveryInstructionsField, fieldState: deliveryInstructionsState } = useController<
    CheckoutFormState,
    "deliveryInstructions"
  >({
    control,
    name: "deliveryInstructions",
  });
  const { field: countryField, fieldState: countryState } = useController<
    CheckoutFormState,
    "country"
  >({
    control,
    name: "country",
    rules: { validate: validateRequiredString },
  });
  const { field: stateField, fieldState: stateState } = useController<
    CheckoutFormState,
    "citySubdivision1"
  >({
    control,
    name: "citySubdivision1",
    rules: { validate: validateRequiredString },
  });
  const { field: cityField, fieldState: cityState } = useController<CheckoutFormState, "city">({
    control,
    name: "city",
    rules: { validate: validateRequiredString },
  });
  const { field: countryCodeField, fieldState: countryCodeState } = useController<
    CheckoutFormState,
    "countryCode"
  >({
    control,
    name: "countryCode",
    rules: { validate: validateRequiredString },
  });
  const { field: shippingConfirmationField, fieldState: shippingConfirmationState } = useController<
    CheckoutFormState,
    "shippingDetailsConfirmationCheckbox"
  >({
    control,
    name: "shippingDetailsConfirmationCheckbox",
    rules: { required: REQUIRED_FIELD_ERROR },
  });
  const {
    cities,
    countries,
    handleCountryChange,
    isCountyEnabled,
    isStatesLoading,
    isCitiesLoading,
    phoneCodes,
    states,
    username,
  } = useChangeShippingDetailsCard({
    country: countryField.value,
    state: stateField.value,
    onCountryChange: countryField.onChange,
  });
  const { field: countyField, fieldState: countyState } = useController<
    CheckoutFormState,
    "county"
  >({
    control,
    name: "county",
    rules: { validate: (value) => !isCountyEnabled || validateRequiredString(value) },
  });

  return (
    <Card data-testid="shipping-details-card" padding="S300" width="100%">
      <Col gridGap="S300" width="100%">
        <Col>
          <Text fontSize="T400" fontWeight="bold" color="neutral90">
            {getString(`${STRING_PATH}.step2`)}
          </Text>
          <Text fontSize="T200" color="neutral90">
            {getString(`${STRING_PATH}.description`)}
          </Text>
        </Col>
        <Col gridGap="S200" width="85%">
          <FormField
            testId="alias-input"
            label={getString(`${STRING_PATH}.alias`)}
            value={username ?? ""}
            required
            disabled
          />
          <FormField
            label={getString(`${STRING_PATH}.fullName`)}
            value={fullNameField.value}
            onChange={fullNameField.onChange}
            onBlur={fullNameField.onBlur}
            inputRef={fullNameField.ref}
            error={fullNameState.error?.message}
            testId="full-name-input"
            required
          />
          <Row gridGap="S300" data-testid="address-row-phone">
            <Col gridGap="S200" width={150}>
              <InputHeader
                labelText={getString(`${STRING_PATH}.countryCode`)}
                htmlFor="select-countrycode"
                required
                error={!!countryCodeState.error}
              />
              <Select
                id="select-countrycode"
                dataTestId="select-countrycode"
                labelId="select-countrycode"
                isSearchable
                isMulti={false}
                placeholder="code"
                options={phoneCodes}
                value={`+ ${countryCodeField.value}`}
                onChange={(value) => countryCodeField.onChange(value?.replace(/^\+\s*/, "") ?? "")}
              />
            </Col>
            <Col width="100%">
              <FormField
                label={getString(`${STRING_PATH}.phoneNumber`)}
                value={phoneNumberField.value}
                onChange={phoneNumberField.onChange}
                onBlur={phoneNumberField.onBlur}
                inputRef={phoneNumberField.ref}
                error={phoneNumberState.error?.message}
                testId="phone-number-input"
                required
              />
            </Col>
          </Row>
          <MessageBanner>{getString(`${STRING_PATH}.banner`)}</MessageBanner>
          <Row gridGap="S300" data-testid="address-row-country-state">
            <Col gridGap="S200" width="50%">
              <InputHeader
                labelText={getString(`${STRING_PATH}.country`)}
                htmlFor="select-country"
                required
                error={!!countryState.error}
              />
              <Select
                id="select-country"
                dataTestId="country-input"
                isSearchable
                isMulti={false}
                width="100%"
                options={countries.map((candidate) => candidate.name)}
                value={countryField.value}
                onChange={(value) => handleCountryChange(value ?? "")}
              />
              {countryState.error?.message && (
                <InputFooter id="country-input-error" error data-testid="country-input-error">
                  {countryState.error.message}
                </InputFooter>
              )}
            </Col>
            <Col gridGap="S200" width="50%">
              <ComboBoxInput
                label={getString(`${STRING_PATH}.stateProvinceRegion`)}
                testId="state-input"
                value={stateField.value}
                options={states}
                isLoading={isStatesLoading}
                required
                error={stateState.error?.message}
                onChange={stateField.onChange}
              />
            </Col>
          </Row>
          <Row gridGap="S300" data-testid="address-row-city-county">
            <Col gridGap="S200" width={isCountyEnabled ? "50%" : "100%"}>
              <ComboBoxInput
                label={getString(`${STRING_PATH}.city`)}
                testId="city-input"
                value={cityField.value}
                options={cities}
                isLoading={isCitiesLoading}
                required
                error={cityState.error?.message}
                onChange={cityField.onChange}
              />
            </Col>
            {isCountyEnabled && (
              <Col width="50%">
                <FormField
                  label={getString(`${STRING_PATH}.county`)}
                  value={countyField.value}
                  onChange={countyField.onChange}
                  onBlur={countyField.onBlur}
                  inputRef={countyField.ref}
                  error={countyState.error?.message}
                  testId="county-input"
                  required
                />
              </Col>
            )}
          </Row>
          <Col width={301}>
            <FormField
              label={getString(`${STRING_PATH}.zipCode`)}
              value={postalCodeField.value}
              onChange={postalCodeField.onChange}
              onBlur={postalCodeField.onBlur}
              inputRef={postalCodeField.ref}
              error={postalCodeState.error?.message}
              testId="postal-code-input"
              required
            />
          </Col>
          <FormField
            label={getString(`${STRING_PATH}.addressLine1`)}
            description={getString(`${STRING_PATH}.line1description`)}
            value={addressLine1Field.value}
            onChange={addressLine1Field.onChange}
            onBlur={addressLine1Field.onBlur}
            inputRef={addressLine1Field.ref}
            error={addressLine1State.error?.message}
            testId="address-line1-input"
            required
          />
          <FormField
            label={getString(`${STRING_PATH}.addressLine2`)}
            description={getString(`${STRING_PATH}.line2description`)}
            value={addressLine2Field.value}
            onChange={addressLine2Field.onChange}
            onBlur={addressLine2Field.onBlur}
            inputRef={addressLine2Field.ref}
            error={addressLine2State.error?.message}
            testId="address-line2-input"
          />
          <FormField
            label={getString(`${STRING_PATH}.addressLine3`)}
            description={getString(`${STRING_PATH}.line3description`)}
            value={addressLine3Field.value}
            onChange={addressLine3Field.onChange}
            onBlur={addressLine3Field.onBlur}
            inputRef={addressLine3Field.ref}
            error={addressLine3State.error?.message}
            testId="address-line3-input"
          />
        </Col>
        <Col width="85%">
          <FormField
            label={getString(`${STRING_PATH}.deliveryInstructions`)}
            value={deliveryInstructionsField.value}
            onChange={deliveryInstructionsField.onChange}
            onBlur={deliveryInstructionsField.onBlur}
            inputRef={deliveryInstructionsField.ref}
            error={deliveryInstructionsState.error?.message}
            placeholder={getString(`${STRING_PATH}.placeholderDeliveryInstructions`)}
            testId="delivery-instructions-input"
            isTextArea
          />
        </Col>
        <Row gridGap="S200">
          <Checkbox
            data-testid="address-confirmation-checkbox"
            checked={shippingConfirmationField.value}
            error={shippingConfirmationState.invalid}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              shippingConfirmationField.onChange(event.target.checked)
            }
          />
          <Label>{getString(`${STRING_PATH}.shippingDetailsConfirmation`)}</Label>
        </Row>
      </Col>
    </Card>
  );
};
